import { useCallback } from "react";

import {
  useNavigate,
  useSearch,
  type NavigateOptions,
  type RegisteredRouter,
} from "@tanstack/react-router";

export type SearchParser<TSearch extends object> = (
  search: Record<string, unknown>,
) => TSearch;

export type SearchPatch<TSearch extends object> = {
  readonly [Key in keyof TSearch]?: TSearch[Key] | undefined;
};

export type SearchUpdater<TSearch extends object> = (
  previous: TSearch,
) => TSearch;

export function mergeSearch<TSearch extends object>(
  patch: SearchPatch<TSearch>,
): SearchUpdater<TSearch> {
  return (previous) => ({ ...previous, ...patch });
}

export function useTypedSearch<TSearch extends object>(
  parseSearch: SearchParser<TSearch>,
): TSearch {
  const search = useSearch({ strict: false });
  return parseSearch(search);
}

export interface TypedNavigateOptions<
  TSearch extends object,
  TTo extends string = string,
  TParams extends object = Record<string, string>,
> {
  readonly hashScrollIntoView?: boolean | ScrollIntoViewOptions;
  readonly ignoreBlocker?: boolean;
  readonly params?: TParams | true | ((previous: TParams) => TParams);
  readonly reloadDocument?: boolean;
  readonly replace?: boolean;
  readonly resetScroll?: boolean;
  readonly search?: true | TSearch | SearchUpdater<TSearch>;
  readonly to?: TTo;
  readonly unsafeRelative?: "path";
  readonly viewTransition?: boolean;
}

export type TypedNavigate<
  TSearch extends object,
  TTo extends string = string,
  TParams extends object = Record<string, string>,
> = (options: TypedNavigateOptions<TSearch, TTo, TParams>) => Promise<void>;

export function useTypedNavigate<
  TSearch extends object,
  TTo extends string = string,
  TParams extends object = Record<string, string>,
>(): TypedNavigate<TSearch, TTo, TParams> {
  const navigate = useNavigate();
  return useCallback(
    (options) =>
      navigate(
        options as NavigateOptions<
          RegisteredRouter,
          string,
          string | undefined
        >,
      ),
    [navigate],
  );
}
